import { component } from "picoapp";

interface DropdownState {
  activeItemIndex: number;
  caretElement: HTMLElement | null;
  isOpen: boolean;
  menuElement: HTMLElement | null;
  menuItems: HTMLElement[];
  totalItems: number;
  triggerElement: HTMLElement | null;
}

export const desktopNavigation = component((node: HTMLElement) => {
  const navMainLinkElements =
    node.querySelectorAll<HTMLElement>(".js-main-nav-link");
  const desktopDropdownContentElements = node.querySelectorAll<HTMLElement>(
    ".js-desktop-nav-dropdown-content"
  );
  const navCaretToggleElements = node.querySelectorAll<HTMLElement>(
    ".js-nav-caret-toggle"
  );
  const overlay = document.querySelector(".js-desktop-overlay");

  let openDesktopNav: string | null = null;
  const dropdownStates = new Map<string, DropdownState>();

  // Initialize dropdown states
  const initializeDropdownStates = () => {
    navMainLinkElements.forEach((link) => {
      const dropdownId = link.dataset.dropdownId;

      if (dropdownId) {
        const menuElement = node.querySelector<HTMLElement>(
          `.js-desktop-nav-dropdown[data-dropdown-id="${dropdownId}"]`
        );
        const menuItems =
          menuElement?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [];
        const caretElement = node.querySelector<HTMLElement>(
          `.js-nav-caret-toggle[data-dropdown-id="${dropdownId}"]`
        );

        dropdownStates.set(dropdownId, {
          isOpen: false,
          activeItemIndex: -1,
          totalItems: menuItems.length,
          triggerElement: link,
          caretElement: caretElement || null,
          menuElement: menuElement || null,
          menuItems: Array.from(menuItems),
        });
      }
    });
  };

  // Note: Screen reader announcements removed - dropdowns are hidden from screen readers (aria-hidden="true")
  // Screen reader users navigate via main links to full pages with sidebar navigation

  // Handlers
  const setDropdownExpanded = (state: DropdownState, expanded: boolean) => {
    state.isOpen = expanded;
    // Update aria-expanded for visual state (CSS targeting)
    state.triggerElement?.setAttribute("aria-expanded", String(expanded));
    state.caretElement?.setAttribute("aria-expanded", String(expanded));
    // Note: aria-hidden stays "true" - dropdowns hidden from screen readers
  };

  const toggleDesktopDropdown = (
    navDropdown: HTMLElement,
    open: boolean,
    animate = true
  ) => {
    if (!animate) {
      navDropdown.classList.remove("transition-height");
      navDropdown.classList.remove("duration-200");
    } else {
      navDropdown.classList.add("transition-height");
      navDropdown.classList.add("duration-200");
    }

    if (open) {
      navDropdown.style.height = "402px";
      // Keep aria-hidden="true" permanently - dropdowns are hidden from screen readers
      overlay?.classList.add("mg:block");
      navDropdown.classList.remove("invisible");
    } else {
      navDropdown.style.height = "0px";
      // Keep aria-hidden="true" permanently - dropdowns are hidden from screen readers
      overlay?.classList.remove("mg:block");
      navDropdown.classList.add("invisible");
    }
  };

  const openDesktopDropdownHandler = (e: KeyboardEvent | MouseEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }

    const dropdownId = e.currentTarget.dataset.dropdownId;

    const desktopNavDropdown = node.querySelector<HTMLElement>(
      `.js-desktop-nav-dropdown[data-dropdown-id=${dropdownId}]`
    );

    if (!desktopNavDropdown) {
      return;
    }

    if (!openDesktopNav) {
      toggleDesktopDropdown(desktopNavDropdown, true);
    } else {
      toggleDesktopDropdown(desktopNavDropdown, true, false);
    }

    const state = dropdownId ? dropdownStates.get(dropdownId) : undefined;
    if (state) {
      setDropdownExpanded(state, true);
    }

    openDesktopNav = dropdownId || null;
  };

  const closeDesktopDropdownHandler = (e: KeyboardEvent | MouseEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) {
      return;
    }

    const dropdownId = e.currentTarget.dataset.dropdownId;

    const desktopNavDropdown = node.querySelector<HTMLElement>(
      `.js-desktop-nav-dropdown[data-dropdown-id=${dropdownId}]`
    );

    if (!desktopNavDropdown) {
      return;
    }

    const state = dropdownId ? dropdownStates.get(dropdownId) : undefined;

    if (state) {
      setDropdownExpanded(state, false);
    }

    if (
      e instanceof MouseEvent &&
      e.relatedTarget instanceof HTMLElement &&
      [...navMainLinkElements, ...desktopDropdownContentElements].includes(
        e.relatedTarget
      )
    ) {
      toggleDesktopDropdown(desktopNavDropdown, false, false);
    } else {
      toggleDesktopDropdown(desktopNavDropdown, false);
      openDesktopNav = null;
    }
  };

  // Helper function to check if menu items exist
  const hasMenuItems = (state: DropdownState): boolean => {
    return state.menuItems.length > 0;
  };

  // Enhanced keyboard navigation functions
  const focusNextItem = (state: DropdownState) => {
    if (!hasMenuItems(state)) return;

    // Reset tabindex on current item
    if (state.activeItemIndex >= 0) {
      state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "-1");
    }

    state.activeItemIndex = Math.min(
      state.activeItemIndex + 1,
      state.menuItems.length - 1
    );

    // Set tabindex to 0 for the new active item
    state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "0");
    state.menuItems[state.activeItemIndex]?.focus();
  };

  const focusPreviousItem = (state: DropdownState) => {
    if (!hasMenuItems(state)) return;

    // Reset tabindex on current item
    if (state.activeItemIndex >= 0) {
      state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "-1");
    }

    state.activeItemIndex = Math.max(state.activeItemIndex - 1, 0);

    // Set tabindex to 0 for the new active item
    state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "0");
    state.menuItems[state.activeItemIndex]?.focus();
  };

  const focusFirstItem = (state: DropdownState) => {
    if (!hasMenuItems(state)) return;

    // Reset tabindex on current item
    if (state.activeItemIndex >= 0) {
      state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "-1");
    }

    state.activeItemIndex = 0;

    // Set tabindex to 0 for the first item
    state.menuItems[0]?.setAttribute("tabindex", "0");
    state.menuItems[0]?.focus();
  };

  const focusLastItem = (state: DropdownState) => {
    if (!hasMenuItems(state)) return;

    // Reset tabindex on current item
    if (state.activeItemIndex >= 0) {
      state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "-1");
    }

    state.activeItemIndex = state.menuItems.length - 1;

    // Set tabindex to 0 for the last item
    state.menuItems[state.activeItemIndex]?.setAttribute("tabindex", "0");
    state.menuItems[state.activeItemIndex]?.focus();
  };

  const activateCurrentItem = (state: DropdownState) => {
    const currentItem = state.menuItems[state.activeItemIndex];
    if (currentItem) {
      currentItem.click();
      closeDropdown(state);
    }
  };

  const openDropdown = (state: DropdownState) => {
    if (!state.menuElement) return;

    state.activeItemIndex = 0;

    setDropdownExpanded(state, true);

    // Show menu
    toggleDesktopDropdown(state.menuElement, true);

    // Focus first item for sighted keyboard users
    if (state.menuItems.length > 0) {
      // Set tabindex to 0 for the first item to make it focusable
      state.menuItems[0]?.setAttribute("tabindex", "0");
      state.menuItems[0]?.focus();
    }

    openDesktopNav = state.triggerElement?.dataset.dropdownId || null;
  };

  const closeDropdown = (
    state: DropdownState,
    { returnFocus = true }: { returnFocus?: boolean } = {}
  ) => {
    if (!state.menuElement) return;

    state.activeItemIndex = -1;

    // Reset tabindex on all menu items
    state.menuItems.forEach((item) => {
      item.setAttribute("tabindex", "-1");
    });

    setDropdownExpanded(state, false);

    // Hide menu
    toggleDesktopDropdown(state.menuElement, false);

    // Return focus to trigger for sighted keyboard users. Skipped when Tab is
    // naturally carrying focus out of the dropdown, so we don't fight the browser's
    // own focus movement (see the "Tab" case in enhancedMenuKeyHandler).
    if (returnFocus) {
      state.triggerElement?.focus();
    }

    openDesktopNav = null;
  };

  const openDropdownAndFocusLast = (state: DropdownState) => {
    if (!state.menuElement) return;

    state.activeItemIndex = state.menuItems.length - 1;

    setDropdownExpanded(state, true);

    // Show menu
    toggleDesktopDropdown(state.menuElement, true);

    // Focus last item for sighted keyboard users
    if (state.menuItems.length > 0) {
      // Set tabindex to 0 for the last item to make it focusable
      state.menuItems[state.menuItems.length - 1]?.setAttribute(
        "tabindex",
        "0"
      );
      state.menuItems[state.menuItems.length - 1]?.focus();
    }

    openDesktopNav = state.triggerElement?.dataset.dropdownId || null;
  };

  const focusPreviousNavItem = (currentElement: HTMLElement) => {
    const currentIndex =
      Array.from(navMainLinkElements).indexOf(currentElement);
    if (currentIndex > 0) {
      const previousElement = navMainLinkElements[currentIndex - 1];
      previousElement.focus();
    } else {
      // Wrap to last item
      const lastElement = navMainLinkElements[navMainLinkElements.length - 1];
      lastElement.focus();
    }
  };

  const focusNextNavItem = (currentElement: HTMLElement) => {
    const currentIndex =
      Array.from(navMainLinkElements).indexOf(currentElement);
    if (currentIndex < navMainLinkElements.length - 1) {
      const nextElement = navMainLinkElements[currentIndex + 1];
      nextElement.focus();
    } else {
      // Wrap to first item
      const firstElement = navMainLinkElements[0];
      firstElement.focus();
    }
  };

  // Enhanced keyboard handler for trigger links
  const enhancedTriggerKeyHandler = (e: KeyboardEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) return;

    const { key } = e;
    const dropdownId = e.currentTarget.dataset.dropdownId;

    if (!dropdownId) return;

    const state = dropdownStates.get(dropdownId);
    if (!state) {
      return;
    }

    switch (key) {
      // Note: Enter/Space intentionally NOT handled - allows main link navigation
      // Screen reader users: Press Enter/Space → Navigate to main page
      // Sighted keyboard users: Press Enter/Space → Navigate to main page
      // Use Arrow Down/Up to open dropdown instead
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        openDropdown(state);
        break;
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        openDropdownAndFocusLast(state);
        break;
      case "ArrowLeft":
        e.preventDefault();
        e.stopPropagation();
        focusPreviousNavItem(e.currentTarget);
        break;
      case "ArrowRight":
        e.preventDefault();
        e.stopPropagation();
        focusNextNavItem(e.currentTarget);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        closeDropdown(state);
        break;
    }
  };

  // Enhanced keyboard handler for menu navigation
  const enhancedMenuKeyHandler = (e: KeyboardEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) return;

    const { key } = e;
    const dropdownId = e.currentTarget.dataset.dropdownId;
    if (!dropdownId) return;

    const state = dropdownStates.get(dropdownId);
    if (!state) return;

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        focusNextItem(state);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusPreviousItem(state);
        break;
      case "Home":
        e.preventDefault();
        focusFirstItem(state);
        break;
      case "End":
        e.preventDefault();
        focusLastItem(state);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        activateCurrentItem(state);
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown(state);
        break;
      case "Tab":
        // Tab moves between menu items like ArrowDown/ArrowUp while there's
        // a next/previous item to go to. Only at the first/last item does it
        // fall through to natively carrying focus out of the dropdown.
        if (e.shiftKey) {
          if (state.activeItemIndex > 0) {
            e.preventDefault();
            focusPreviousItem(state);
          } else {
            closeDropdown(state, { returnFocus: false });
          }
        } else if (state.activeItemIndex < state.menuItems.length - 1) {
          e.preventDefault();
          focusNextItem(state);
        } else {
          closeDropdown(state, { returnFocus: false });
        }
        break;
    }
  };

  const caretToggleHandler = (e: MouseEvent) => {
    if (!(e.currentTarget instanceof HTMLElement)) return;

    const dropdownId = e.currentTarget.dataset.dropdownId;
    if (!dropdownId) return;

    const state = dropdownStates.get(dropdownId);
    if (!state) return;

    const isCurrentlyOpen = !state.menuElement?.classList.contains("invisible");

    if (isCurrentlyOpen) {
      closeDropdown(state);
    } else {
      openDropdown(state);
    }
  };

  // Initialize dropdown states
  initializeDropdownStates();

  // Setup Listeners

  [...navMainLinkElements, ...desktopDropdownContentElements].forEach(
    (link) => {
      link.addEventListener("mouseenter", openDesktopDropdownHandler);
      link.addEventListener("mouseleave", closeDesktopDropdownHandler);
    }
  );

  desktopDropdownContentElements.forEach((dropdown) => {
    dropdown.addEventListener("keydown", enhancedMenuKeyHandler);
  });

  navMainLinkElements.forEach((link) => {
    link.addEventListener("keydown", enhancedTriggerKeyHandler);
  });

  navCaretToggleElements.forEach((caret) => {
    caret.addEventListener("click", caretToggleHandler);
  });

  return () => {
    [...navMainLinkElements, ...desktopDropdownContentElements].forEach(
      (link) => {
        link.removeEventListener("mouseenter", openDesktopDropdownHandler);
        link.removeEventListener("mouseleave", closeDesktopDropdownHandler);
      }
    );
    navMainLinkElements.forEach((link) => {
      link.removeEventListener("keydown", enhancedTriggerKeyHandler);
    });
    desktopDropdownContentElements.forEach((dropdown) => {
      dropdown.removeEventListener("keydown", enhancedMenuKeyHandler);
    });
    navCaretToggleElements.forEach((caret) => {
      caret.removeEventListener("click", caretToggleHandler);
    });
  };
});
